import { PropertySavedModel } from 'common/data/property-saved/model';
import { dateToIso } from 'common/helper/date/to-iso';
import { PropertySavedStatechart } from 'common/module/property/saved/statechart';
import { PropertySavedStatechartStoreEvent } from 'common/module/property/saved/statechart/store.event';
import { PropertySavedStatechartStoreActionEnum } from 'common/module/property/saved/statechart/store-action.enum';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { PropertySavedStoreServiceInterface } from 'common/service/property-saved-store/service.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

export class PropertySavedStatechartStore {
  /**
   * Constructor
   */
  constructor(
    private statechartStore: StatechartStoreInterface,
    private propertySavedStoreService: PropertySavedStoreServiceInterface,
    private userAuthentication: UserAuthenticationServiceInterface
  ) {}

  /**
   * Initialize store
   */
  public initialize(): void {
    this.statechartStore.initialize({
      statechart: PropertySavedStatechart,
    });

    this.observers();
  }

  /**
   * Save
   */
  public save(propertyId: string): void {
    const model = new PropertySavedModel();

    model.property_id = parseInt(propertyId, 10);
    model.save_date = dateToIso(new Date());
    this.statechartStore.transit({
      event: PropertySavedStatechartStoreEvent.save,
      payload: model,
    });
  }

  /**
   * Unsave
   */
  public unsave(propertyId: string): void {
    this.statechartStore.transit({
      event: PropertySavedStatechartStoreEvent.unsave,
      payload: propertyId,
    });
  }

  /**
   * Destroy store
   */
  public destroy(): void {
    this.userAuthentication
      .getEventEmitter()
      .removeListener(UserAuthenticationEvent.logOutSucceeded, this.onLogOutSucceeded);

    this.statechartStore
      .getEventEmitter()
      .removeListener(PropertySavedStatechartStoreActionEnum.sendSaveRequest, this.onSendSaveRequestAction);

    this.statechartStore
      .getEventEmitter()
      .removeListener(PropertySavedStatechartStoreActionEnum.sendUnsaveRequest, this.onSendUnsaveRequestAction);

    this.statechartStore
      .getEventEmitter()
      .removeListener(PropertySavedStatechartStoreActionEnum.addToLocalStorage, this.onAddToLocalStorageAction);

    this.statechartStore
      .getEventEmitter()
      .removeListener(
        PropertySavedStatechartStoreActionEnum.removeFromLocalStorage,
        this.onRemoveFromLocalStorageAction
      );
  }

  /**
   * Reset statechart to idle state
   */
  public reset(): void {
    this.statechartStore.transit({
      event: PropertySavedStatechartStoreEvent.reset,
      payload: null,
    });
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.userAuthentication
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.logOutSucceeded, this.onLogOutSucceeded);

    this.statechartStore
      .getEventEmitter()
      .addListener(PropertySavedStatechartStoreActionEnum.sendSaveRequest, this.onSendSaveRequestAction);

    this.statechartStore
      .getEventEmitter()
      .addListener(PropertySavedStatechartStoreActionEnum.sendUnsaveRequest, this.onSendUnsaveRequestAction);

    this.statechartStore
      .getEventEmitter()
      .addListener(PropertySavedStatechartStoreActionEnum.addToLocalStorage, this.onAddToLocalStorageAction);

    this.statechartStore
      .getEventEmitter()
      .addListener(PropertySavedStatechartStoreActionEnum.removeFromLocalStorage, this.onRemoveFromLocalStorageAction);
  }

  /**
   * Send request save request action handler
   */
  private onSendSaveRequestAction = (model: PropertySavedModel): void => {
    this.propertySavedStoreService.save(String(model.property_id));
  };

  /**
   * Send unsave request action handler
   */
  private onSendUnsaveRequestAction = (propertyId: string): void => {
    this.propertySavedStoreService.delete(propertyId);
  };

  /**
   * Add to local storage action handler
   */
  private onAddToLocalStorageAction = (model: PropertySavedModel) => {
    this.propertySavedStoreService.saveInDb(model);

    this.statechartStore.transit({
      event: PropertySavedStatechartStoreEvent.saveSuccess,
      payload: this.propertySavedStoreService.getAllFromCache(),
    });
  };

  /**
   * Remove from localstorage action handler
   */
  private onRemoveFromLocalStorageAction = (propertyId: string) => {
    this.propertySavedStoreService.unsave(propertyId);

    this.statechartStore.transit({
      event: PropertySavedStatechartStoreEvent.unsaveSuccess,
      payload: this.propertySavedStoreService.getAllFromCache(),
    });
  };

  /**
   * Log out succeeded
   */
  private onLogOutSucceeded = () => {
    this.reset();
  };
}
